import { isTruthy } from '~/api/utils/isTruthy'
import { range } from 'ramda'
import { PropertyMashup, TrainMashup } from '~/api/routes/domestic/order/controller/type'
import { periodWithDayOrWeekFormat } from '~/api/utils/datetime'
import {
  CancelPolicyType,
  ICancelPolicy,
} from '~/api/converters/domestic/order/ProductSection/Property/index.type'
import { DateTime } from 'luxon'

export const getDomesticTrainPassengersText = ({
  adults,
  child,
}: {
  adults: number
  child: number
}): string => {
  return [`성인 ${adults}`, child ? `아동 ${child}` : ''].filter(isTruthy).join(', ')
}

export const lpad = (value: number | string, figures: number, char?: string) => {
  const c = char || '0'
  const s = String(value)
  if (s.length >= figures) {
    return s
  }
  const added = figures - s.length
  const padStr = range(0, added)
    .map(() => c)
    .join('')
  return `${padStr}${s}`
}

export const getDomesticTrainNoText = ({
  stationTrainClassifyName,
  trainNo,
}: {
  stationTrainClassifyName: string
  trainNo: string
}) => {
  const paddedTrainNo = lpad(trainNo, 4)
  const displayTrainNo = paddedTrainNo.substring(paddedTrainNo.length - 4, paddedTrainNo.length)
  return `${stationTrainClassifyName}-${displayTrainNo}`
}

export const getDomesticTrainSeatDescription = (
  seats: { seatDescription: string; carriageNo?: string }[]
): string => {
  const seatDescriptions = seats.map(s => s.seatDescription).join(',')
  // 동일한 호차내의 좌석만 선택 가능
  const carriageNoText = seats[0]?.carriageNo ? `${+seats[0].carriageNo}호차` : '임의배정'

  return [carriageNoText, seatDescriptions].filter(isTruthy).join(' ')
}

export const mergeDomesticTrainSeatText = (texts: string[]) => {
  return texts.filter(isTruthy).join(' | ')
}

export type TGetTrainPackageDiscountAmountParams = {
  price: number
  packageDiscount: number
}[]

// 묶음할인을 계산하기 위해 주문서 상품들을 TGetTrainPackageDiscountAmountParams 타입으로 재구성하는 함수
export const mapTrainPackageDiscountOptionsOnOrder = ({
  trainMashup,
  propertyMashup,
}: {
  trainMashup: TrainMashup
  propertyMashup: PropertyMashup
}): TGetTrainPackageDiscountAmountParams => {
  const mapPropertyMashup = propertyMashup.map(property => {
    return {
      price: property.price || 0,
      packageDiscount: 0,
    }
  })

  const mapTrainMashup = trainMashup
    .map(train => {
      const { single, returns } = train
      const result = [
        {
          price: single.price || 0,
          packageDiscount: single.packageDiscountAmount || 0,
        },
      ]

      if (returns) {
        result.push({
          price: returns.price || 0,
          packageDiscount: returns.packageDiscountAmount || 0,
        })
      }

      return result
    })
    .flat()

  return [...mapPropertyMashup, ...mapTrainMashup]
}

/**
 * @Summary       KORAIL 및 사내 정책을 따르는 묶음할인 가격을 계산하여 반환하는 함수
 * @Description   승차권 별 묶음할인율이 1% 이상인 경우만 할인 적용 대상이다.
 * @param         {TGetTrainPackageDiscountAmountParams} mappedOrders 묶음할인 계산을 위해 재구성된 주문서 상품들
 * @return        {number} 최종 묶음할인 가격. 0이라면 묶음할인 적용 대상이 아니다
 * */
export function getTrainPackageDiscountAmount(
  mappedOrders: TGetTrainPackageDiscountAmountParams
): number {
  let totalPackageDiscount = 0
  const optionPriceWithoutDiscountedTrain = mappedOrders.reduce((acc, cur) => {
    // 승차권 별 묶음할인율이 1% 이상인 경우만 총 묶음할인가격에 대해준다.
    if (Math.round((cur.packageDiscount / cur.price) * 100) > 0) {
      totalPackageDiscount += cur.packageDiscount
      return acc
    }
    return acc + cur.price
  }, 0)
  return Math.min(totalPackageDiscount, optionPriceWithoutDiscountedTrain)
}

export const getTrainTicketName = (train: TrainMashup[number]) => {
  return `[${train.returns ? '왕복' : '편도'}] ${train.single.departureStationName} - ${
    train.single.arrivalStationName
  }`
}

export const getTrainTicketDurationText = (way: TrainMashup[number]['single']) => {
  return periodWithDayOrWeekFormat({
    from: { date: way.departureDate, time: way.departureTime },
    to: `${way.arrivalDate}T${way.arrivalTime}`,
  })
}

export function transformTrainNo(trainNo: string) {
  if (!trainNo) {
    return '0000'
  }
  const paddedTrainNo = lpad(trainNo, 4)
  return paddedTrainNo.substr(paddedTrainNo.length - 4, paddedTrainNo.length)
}

export const getTrainTicketOptions = (way: TrainMashup[number]['single']) => {
  const seatsText = way.seats.map(s => s.seatDescription).join(',')
  const carriageNo = way.seats[0]?.carriageNo
  const carriageNoText = carriageNo == null ? '임의배정' : `${+carriageNo}호차`

  return [
    getDomesticTrainNoText({
      stationTrainClassifyName: way.stationTrainClassifyName,
      trainNo: way.trainNo,
    }),
    `${carriageNoText} ${seatsText}`,
  ]
}

export const trainCancelPolicyHelper = (
  departureDate: string,
  departureTime: string,
  currentAt: string
): ICancelPolicy => {
  // 출발 3일 전 까지	없음
  // 출발 2일 전	예매 금액의 10%
  // 출발 1일 전	예매 금액의 20%
  // 출발 당일 ~ 출발 시각 이전	예매 금액의 30%
  // 출발 시각 이후	환불 불가

  const curDateTime = DateTime.fromISO(currentAt)
  const departureDateTime = DateTime.fromFormat(departureDate, 'yyyy-MM-dd')
  const ratios = [0, 10, 20, 30, 100]
  const policies = [
    {
      type: CancelPolicyType.FREE,
      weight: ratios[0],
      endAt: departureDateTime.minus({ days: 2 }),
    },
    {
      type: CancelPolicyType.RATIO,
      weight: ratios[1],
      endAt: departureDateTime.minus({ days: 1 }),
    },
    {
      type: CancelPolicyType.RATIO,
      weight: ratios[2],
      endAt: departureDateTime.minus({ days: 0 }),
    },
    {
      type: CancelPolicyType.RATIO,
      weight: ratios[3],
      endAt: DateTime.fromFormat(`${departureDate} ${departureTime}`, 'yyyy-MM-dd HH:mm:ss'),
    },
    {
      type: CancelPolicyType.TOTAL,
      weight: ratios[4],
      endAt: DateTime.fromFormat(`${departureDate} ${departureTime}`, 'yyyy-MM-dd HH:mm:ss'),
    },
  ]

  return policies
    .filter(policy => curDateTime < policy.endAt)
    .map(policy => ({ ...policy, endAt: policy.endAt.toISODate() }))[0]
}
